import React, { useEffect, useRef, useState } from 'react';
import { Button } from './ui/button';
import { Building2, Users, TrendingUp, ArrowRight } from 'lucide-react';
import { submitSchoolForm } from '../data/mock';
import { CDN } from '../data/cdnImages';
import { trackSchoolFormSubmit } from '../lib/gtag';
import { getApiBase } from '@/lib/apiBase';
import { trackEvent } from '@/lib/trackEvent';
import { toast } from 'sonner';

const SchoolsSection = ({ openFormSignal = 0 }) => {
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [formData, setFormData] = useState({
    schoolName: '',
    contactPerson: '',
    email: '',
    phone: '',
    studentCount: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [pendingScrollToForm, setPendingScrollToForm] = useState(false);
  const formStartedRef = useRef(false);
  const formRef = useRef(null);
  /* Seeded with the current value: switching audience remounts this section, and a stale non-zero
     signal must not be mistaken for a fresh CTA click. Only later increments open the form. */
  const handledSignalRef = useRef(openFormSignal);

  useEffect(() => {
    if (openFormSignal === handledSignalRef.current) return;
    handledSignalRef.current = openFormSignal;
    setIsFormOpen(true);
    setPendingScrollToForm(true);
  }, [openFormSignal]);

  /* Scroll only once the form has actually rendered, so we land on it and not on the section heading. */
  useEffect(() => {
    if (!pendingScrollToForm || !isFormOpen) return;
    formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    setPendingScrollToForm(false);
  }, [pendingScrollToForm, isFormOpen]);
  const onSchoolLeadFormInteract = () => {
    if (formStartedRef.current) return;
    formStartedRef.current = true;
    void trackEvent('form_started', { meta: { surface: 'schools_section', source: 'school_partner_form' } });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const web3formsKey = process.env.REACT_APP_WEB3FORMS_KEY || '';
      
      // If no Web3Forms key configured, store in localStorage (development mode)
      if (!web3formsKey || web3formsKey.includes('YOUR_ACCESS_KEY')) {
        const result = await submitSchoolForm(formData);
        toast.success(result.message);
        trackSchoolFormSubmit();
        void trackEvent('form_submitted', { meta: { surface: 'schools_section', channel: 'mock' } });
      } else {
        // Submit to Web3Forms (sends to email)
        const response = await fetch('https://api.web3forms.com/submit', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            access_key: web3formsKey,
            subject: 'School partnership request · ChooseRightCareer',
            from_name: 'ChooseRightCareer School Form',
            school_name: formData.schoolName,
            contact_person: formData.contactPerson,
            email: formData.email,
            phone: formData.phone,
            student_count: formData.studentCount,
          })
        });
        
        const result = await response.json();
        
        if (result.success) {
          toast.success('Thank you! Our team will reach out shortly.');
          trackSchoolFormSubmit();
          try {
            const apiBase = getApiBase();
            await fetch(`${apiBase}/api/landing-leads`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                name: formData.contactPerson || formData.schoolName,
                email: formData.email,
                phone: formData.phone,
                class: `School: ${formData.schoolName} · Est. students: ${formData.studentCount}`,
                source: 'school_web3',
              }),
            });
          } catch {
            /* non-fatal */
          }
        } else {
          toast.error('Something went wrong. Please try again.');
        }
      }
      
      setIsFormOpen(false);
      setFormData({
        schoolName: '',
        contactPerson: '',
        email: '',
        phone: '',
        studentCount: ''
      });
    } catch (error) {
      console.error('Form submission error:', error);
      toast.error('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="py-24 bg-gradient-to-br from-blue-900 via-blue-800 to-teal-900 text-white relative overflow-hidden">
      {/* Decorative elements */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-20 left-20 w-64 h-64 bg-white rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 right-20 w-96 h-96 bg-teal-300 rounded-full blur-3xl"></div>
      </div>

      <div className="container mx-auto px-6 relative z-10">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full mb-6">
              <Building2 className="w-4 h-4" />
              <span className="text-sm font-semibold text-white">For schools and institutions</span>
         
            </div>
            
            <h2 className="font-display text-4xl lg:text-5xl font-bold mb-6 tracking-tight">
              Bring Career DNA to your campus without adding chaos to the timetable
            </h2>
            <p className="text-xl text-blue-100 max-w-3xl mx-auto">
              Structured assessments for Class 9 to 12, cohort ready analytics, and PDFs families actually read. We
              help you launch a pilot, prove impact, then scale with the same framework your counsellors already know.
            </p>
          </div>

          <div className="max-w-5xl mx-auto mb-12 rounded-2xl overflow-hidden border border-white/25 shadow-2xl ring-1 ring-white/10">
            <img
              src={CDN.schoolPartnerHero}
              alt="School and campus setting where institutions roll out structured career guidance"
              width={1600}
              height={640}
              loading="lazy"
              decoding="async"
              className="w-full h-48 sm:h-56 md:h-64 lg:h-72 object-cover"
            />
          </div>

          <div className="grid md:grid-cols-3 gap-8 mb-12">
            <div className="bg-white/10 backdrop-blur-md p-8 rounded-2xl border border-white/20">
              <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center mb-4">
                <Users className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-xl font-bold mb-3">Bulk assessments that still feel personal</h3>
              <p className="text-blue-100">
                Run entire batches on one Career DNA spine so every learner gets the same quality of insight, not a
                patchwork of ad hoc worksheets.
              </p>
            </div>

            <div className="bg-white/10 backdrop-blur-md p-8 rounded-2xl border border-white/20">
              <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center mb-4">
                <TrendingUp className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-xl font-bold mb-3">Cohort signals leadership can use</h3>
              <p className="text-blue-100">
                Spot trends across grades, streams, and interest clusters so you can brief trustees or parent bodies
                with numbers, not vibes alone.
              </p>
            </div>

            <div className="bg-white/10 backdrop-blur-md p-8 rounded-2xl border border-white/20">
              <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center mb-4">
                <Building2 className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-xl font-bold mb-3">Integration that respects your ops team</h3>
              <p className="text-blue-100">
                Dedicated partner support, rollout templates, and clear checkpoints so IT, academics, and counselling
                stay aligned from week one.
              </p>
            </div>
          </div>

          {!isFormOpen ? (
            <div className="text-center">
              <Button
                onClick={() => {
                  void trackEvent('cta_clicked', { meta: { surface: 'schools_section', action: 'open_pilot_form' } });
                  setIsFormOpen(true);
                }}
                size="lg"
                className="bg-white text-blue-900 hover:bg-blue-50 shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:scale-105 text-lg px-8 py-6"
              >
                Talk to us about a pilot
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </div>
          ) : (
            <div
              ref={formRef}
              className="bg-white/10 backdrop-blur-md rounded-3xl p-8 border border-white/20 max-w-2xl mx-auto scroll-mt-24"
            >
              <h3 className="text-2xl font-bold mb-6">Tell us about your school</h3>
              <form onSubmit={handleSubmit} onFocusCapture={onSchoolLeadFormInteract} className="space-y-6">
                <div>
                  <input
                    type="text"
                    placeholder="School Name"
                    required
                    value={formData.schoolName}
                    onChange={(e) => setFormData({...formData, schoolName: e.target.value})}
                    className="w-full px-4 py-3 rounded-xl bg-white/20 backdrop-blur-sm border border-white/30 text-white placeholder-blue-200 focus:outline-none focus:ring-2 focus:ring-white/50 transition-all duration-300"
                  />
                </div>
                <div>
                  <input
                    type="text"
                    placeholder="Contact Person Name"
                    required
                    value={formData.contactPerson}
                    onChange={(e) => setFormData({...formData, contactPerson: e.target.value})}
                    className="w-full px-4 py-3 rounded-xl bg-white/20 backdrop-blur-sm border border-white/30 text-white placeholder-blue-200 focus:outline-none focus:ring-2 focus:ring-white/50 transition-all duration-300"
                  />
                </div>
                <div className="grid md:grid-cols-2 gap-6">
                  <input
                    type="email"
                    placeholder="Email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    className="w-full px-4 py-3 rounded-xl bg-white/20 backdrop-blur-sm border border-white/30 text-white placeholder-blue-200 focus:outline-none focus:ring-2 focus:ring-white/50 transition-all duration-300"
                  />
                  <input
                    type="tel"
                    placeholder="Phone Number"
                    required
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    className="w-full px-4 py-3 rounded-xl bg-white/20 backdrop-blur-sm border border-white/30 text-white placeholder-blue-200 focus:outline-none focus:ring-2 focus:ring-white/50 transition-all duration-300"
                  />
                </div>
                <div>
                  <input
                    type="number"
                    placeholder="Approximate Student Count"
                    required
                    value={formData.studentCount}
                    onChange={(e) => setFormData({...formData, studentCount: e.target.value})}
                    className="w-full px-4 py-3 rounded-xl bg-white/20 backdrop-blur-sm border border-white/30 text-white placeholder-blue-200 focus:outline-none focus:ring-2 focus:ring-white/50 transition-all duration-300"
                  />
                </div>
                <div className="flex gap-4">
                  <Button 
                    type="submit"
                    disabled={isSubmitting}
                    className="flex-1 bg-white text-blue-900 hover:bg-blue-50 transition-all duration-300 py-6"
                  >
                    {isSubmitting ? 'Submitting...' : 'Submit Request'}
                  </Button>
                  <Button 
                    type="button"
                    onClick={() => setIsFormOpen(false)}
                    variant="outline"
                    className="border-white/30 text-white hover:bg-white/10 transition-all duration-300 py-6"
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default SchoolsSection;
